import { Fragment } from 'react';
import { ROUTES } from './routes.js';
import { useApp } from './AppContext.js';
import { useAsync } from '../ui/lib/useAsync.js';
import { StatusDot } from '../ui/components/primitives.js';
import { IconGateway, IconLogout, IconQueue } from '../ui/components/icons.js';

interface LiveReadout {
  up: number;
  total: number;
  emVoo: number;
  dead: number;
  gateway: string;
}

/**
 * Navegação lateral.
 *
 * Agrupa as rotas por seção (a mesma ordem do array) e marca a ativa. O rodapé
 * mostra a marca de AMOSTRA quando a tela não está lendo o banco — é o aviso
 * permanente que impede alguém de operar achando que o número é real.
 */
export function Sidebar({
  rota,
  onNavigate,
  onSair,
}: {
  rota: string;
  onNavigate: (id: string) => void;
  onSair: () => void;
}) {
  const { data } = useApp();
  const secoes = [...new Set(ROUTES.map((r) => r.section))];

  // Readout ao vivo: mesma porta de dados do painel, carregado uma vez no mount
  // (a sidebar persiste entre rotas, então não refaz a cada navegação).
  const live = useAsync<LiveReadout>(
    async () => {
      const [saude, fila, gw] = await Promise.all([data.health(), data.getQueue(), data.getGateway()]);
      return {
        up: saude.filter((s) => s.up).length,
        total: saude.length,
        emVoo: fila.queued + fila.processing,
        dead: fila.dead,
        gateway: gw.active,
      };
    },
    [data],
  );

  return (
    <aside className="sidebar">
      <div className="sidebar__brand">
        <span className="brand-mark">TN</span>
        <span className="sidebar__brand-text">
          <span className="sidebar__brand-name">TELENORTE</span>
          <span className="sidebar__brand-sub">cobrança PIX</span>
        </span>
      </div>

      <nav className="nav">
        {secoes.map((secao) => (
          <Fragment key={secao}>
            <div className="nav__section">{secao}</div>
            {ROUTES.filter((r) => r.section === secao).map((r) => {
              const Icon = r.icon;
              const ativa = r.id === rota;
              return (
                <button
                  key={r.id}
                  className={`nav__item${ativa ? ' nav__item--active' : ''}`}
                  onClick={() => onNavigate(r.id)}
                  aria-current={ativa ? 'page' : undefined}
                >
                  <span className="nav__icon">
                    <Icon size={18} />
                  </span>
                  {r.label}
                </button>
              );
            })}
          </Fragment>
        ))}
      </nav>

      {live.data && (
        <div className="sidebar__live">
          <div className="sidebar__live-title">Ao vivo</div>
          <div className="live-row">
            <StatusDot up={live.data.up === live.data.total} /> Integrações
            <span className="live-row__val">
              {live.data.up}/{live.data.total}
            </span>
          </div>
          <div className="live-row">
            <IconQueue size={14} /> Em voo
            <span className="live-row__val">{live.data.emVoo}</span>
          </div>
          <div className="live-row">
            <IconGateway size={14} /> Gateway
            <span className="live-row__val" style={{ textTransform: 'capitalize' }}>
              {live.data.gateway}
            </span>
          </div>
          {live.data.dead > 0 && (
            <div className="live-row" style={{ color: 'var(--danger)' }}>
              Dead-letter
              <span className="live-row__val" style={{ color: 'var(--danger)' }}>
                {live.data.dead}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="sidebar__foot">
        <button className="nav__item" onClick={onSair}>
          <span className="nav__icon">
            <IconLogout size={18} />
          </span>
          Sair
        </button>
      </div>
    </aside>
  );
}
